/**
 * Links to a project or a task that a person can paste into Slack, WhatsApp
 * or an email.
 *
 * Kyu, 3 September: "I need to be able to have the ability to send a
 * shareable link (to any TAP Hub members) of a project or a task link."
 *
 * ── There is nothing new about the URL ─────────────────────────────────────
 *
 * The Hub has been building these since Stage 11. notificationHref() produces
 * /projects/<project>?task=<task>&comment=<comment>, ProjectPage reads ?task=
 * and ?comment= off the URL and opens the right panel, and the inbox has been
 * linking that way for months. What was missing was any way for a PERSON to
 * get one: the format existed, the button did not.
 *
 * So this file deliberately builds the same shape rather than inventing a
 * second one. Two link formats for one destination is how one of them quietly
 * stops working.
 *
 * ── What a shared link does NOT do ─────────────────────────────────────────
 *
 * It grants nothing. Kyu, asked directly what should happen when the
 * recipient is not a member: "Nothing changes — warn me at copy time."
 *
 * A link is an address, not a key. Projects are members_only by default and
 * the alternative is all_tap; a link that granted access on open would mean a
 * URL pasted into a busy channel hands the project to everyone who clicks it,
 * and the visibility setting Kyu made editable this week would stop meaning
 * anything. Row Level Security decides who can open it, exactly as before.
 *
 * The honest cost of that choice is that you can send a link somebody cannot
 * open. shareAudienceNote() below is the answer: it tells you which case you
 * are in AT THE MOMENT YOU COPY, so a dead link is a decision rather than a
 * surprise.
 */

import type { ProjectVisibility } from '../projects/types';

/**
 * The absolute link to a project.
 *
 * `origin` is passed in rather than read from window so this stays a pure
 * function: the tests pin the exact string, and the string is what gets
 * pasted into somebody's chat window.
 */
export function projectShareUrl(origin: string, projectId: string): string {
  return `${trimOrigin(origin)}/projects/${encodeURIComponent(projectId)}`;
}

/**
 * The absolute link to one task, which is a project link carrying the task.
 *
 * A task has no route of its own and does not need one — it is a thing inside
 * a project, and the panel that shows it is part of the project page. Giving
 * it a top-level URL would mean a second way to reach the same screen.
 */
export function taskShareUrl(origin: string, projectId: string, taskId: string): string {
  return `${projectShareUrl(origin, projectId)}?task=${encodeURIComponent(taskId)}`;
}

/**
 * The absolute link to one day's CEO brief.
 *
 * The date is the identity (ceo_briefs.brief_date is unique), so the link
 * survives re-pasting the same day. Only a super admin can open it; the link
 * grants nothing, like every other link here.
 */
export function briefShareUrl(origin: string, briefDate: string): string {
  return `${trimOrigin(origin)}/ceo-brief/${encodeURIComponent(briefDate)}`;
}

/**
 * The absolute PUBLIC link to the CEO brief. No sign-in, no Google screen.
 *
 * The sibling of briefShareUrl above and deliberately next to it, because
 * confusing the two is not hypothetical: on 14 September the masthead's copy
 * button handed out the Hub address and the recipient landed on the supplier
 * portal. This one carries a bearer token and opens for anybody holding it.
 * The token is minted by create_brief_token and shown once; nothing in the
 * app can read it back, so nothing here tries.
 */
export function briefPortalUrl(origin: string, token: string): string {
  return `${trimOrigin(origin)}/brief/${encodeURIComponent(token)}`;
}

/** The Page Pulse bearer-token route, built in one place for copied links. */
export function pagePulsePortalUrl(origin: string, token: string): string {
  return `${trimOrigin(origin)}/pulse/${encodeURIComponent(token)}`;
}

/** A trailing slash on the origin would produce a double slash in the path. */
function trimOrigin(origin: string): string {
  return origin.endsWith('/') ? origin.slice(0, -1) : origin;
}

/**
 * What to tell the person doing the copying about who will be able to open
 * this.
 *
 * Shown next to the button before and after the copy, not as an error
 * afterwards. The whole point is that it arrives while they can still decide
 * to change the project's visibility or add the person instead.
 */
export function shareAudienceNote(visibility: ProjectVisibility): string {
  return visibility === 'all_tap'
    ? 'Anyone on the TAP team can open it.'
    : "Members only — anyone outside this project won't be able to open it.";
}

/** Whether the note is a warning (styled as one) or just information. */
export function shareAudienceIsLimited(visibility: ProjectVisibility): boolean {
  return visibility !== 'all_tap';
}
